import type { EstadoDoEnvioWhatsapp } from "@foto/shared";

/**
 * O que a tela diz do envio pelo WhatsApp (#105), igual na entrega e na Biblioteca.
 *
 * O envio passou a sair por uma fila: o botão responde na hora, e a foto vai na vez dela. Por isso a tela mostra o
 * estado, e não só "enviada": "na fila" (inclui as novas tentativas, com o motivo da demora), "enviada", ou "não
 * enviada" e por quê.
 */

export interface EnvioNaTela {
  estado: EstadoDoEnvioWhatsapp | null;
  motivo: string | null;
  /** A hora do último envio que deu certo. */
  enviadaEm: Date | string | null;
  /** Para onde foi o último envio que deu certo: o grupo só nos envios de antes da fila (#105). */
  enviadaPara?: "grupo" | "participante" | null;
}

type Tom = "projecao" | "fixado" | "descartar" | "quieto";

function quando(d: Date | string): string {
  return new Date(d).toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** O selo do envio, ou nada quando a foto nunca foi pedida nem enviada. */
export function seloDoEnvio(envio: EnvioNaTela): { tom: Tom; texto: string } | null {
  if (envio.estado === "na_fila") return { tom: "projecao", texto: "na fila do WhatsApp" };
  if (envio.estado === "nao_enviada") return { tom: "descartar", texto: "não enviada" };
  if (envio.enviadaEm) {
    const destino = envio.enviadaPara === "grupo" ? "ao grupo" : "à pessoa";
    return { tom: "fixado", texto: `enviada ${destino} ${quando(envio.enviadaEm)}` };
  }
  return null;
}

/** O que o botão diz: o primeiro envio, outro depois de enviada, ou a nova tentativa depois de não enviada. */
export function rotuloDoEnvio(envio: EnvioNaTela, primeiro = "Enviar no WhatsApp"): string {
  if (envio.estado === "na_fila") return "Na fila do WhatsApp";
  if (envio.estado === "nao_enviada") return "Tentar de novo";
  if (envio.estado === "enviada" || envio.enviadaEm) return "Enviar de novo";
  return primeiro;
}

/** Enquanto está na fila, o botão não pede de novo: o servidor recusaria, e a foto já vai. */
export function podePedirOEnvio(envio: EnvioNaTela): boolean {
  return envio.estado !== "na_fila";
}
